import React, { useState, useCallback, useRef, useEffect } from 'react';
import { APP_ICON_PATHS, APP_ICON_TONES, ICON_PICKER_GROUPS, parseIconChoice } from '@/utils/appIcons';
import './AppPublishModal.css';

// ===== 原型一致的图标库配置 =====
const ICON_GROUPS = ICON_PICKER_GROUPS.map(g => ({
  id: g.label,
  label: g.label,
  items: g.icons.map(icon => ({
    key: g.label + '-' + icon,
    icon,
    label: icon,
  }))
}));

// 按 key 索引图标项
const ICON_BY_KEY = new Map(ICON_GROUPS.flatMap(g => g.items).map(x => [x.key, x]));

// 原型配色（6种，用原型中的 tone-xxx 颜色）
const ICON_TONES = [
  { id: 'ink',   bg: '#30323a', color: 'white',   label: '墨黑' },
  { id: 'paper', bg: '#eeeef1', color: '#30323a', label: '浅灰' },
  { id: 'blue',  bg: '#247cf2', color: 'white',   label: '蓝色' },
  { id: 'sage',  bg: '#91bba6', color: '#264336', label: '鼠尾草绿' },
  { id: 'sand',  bg: '#e5d9c1', color: '#685432', label: '砂金' },
  { id: 'plum',  bg: '#9c8ba8', color: 'white',   label: '淡紫' },
];


// ===== 简单SVG图标 =====
const icons: Record<string, string> = {
  upload: '<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>',
  check: '<polyline points="20 6 9 17 4 12"/>',
  refresh: '<polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>',
  info: '<circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/>',
  sparkles: '<path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/><path d="M5 3v4"/><path d="M19 17v4"/><path d="M3 5h4"/><path d="M17 19h4"/>',
  globe: '<circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>',
  lock: '<rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>',
  edit: '<path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>',
  down: '<polyline points="6 9 12 15 18 9"/>',
  close: '<line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>',
};

function Icon({ name, size = 24 }: { name: string; size?: number }) {
  const path = icons[name] || icons.info;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" dangerouslySetInnerHTML={{ __html: path }} />
  );
}

// ===== 类型 =====
interface CategoryItem {
  id: string;
  name: string;
}

interface Draft {
  name: string;
  desc: string;
  cat: string;
  features: string;
  note: string;
  visibility: 'public' | 'dept';
  apiUrl: string;
  refresh: string;
  iconChoice: {
    icon: string;
    tone: string;
  };
}

interface FileInfo {
  kind: string;
  appType: string;
  title: string;
  description: string;
}

interface AppPublishModalProps {
  mode?: 'create' | 'edit' | 'update';
  existingApp?: {
    id: number | string;
    name: string;
    desc: string;
    cat: string;
    features?: string;
    note?: string;
    visibility?: 'public' | 'dept';
    apiUrl?: string;
    refresh?: string;
    version?: string;
    iconChoice?: { icon: string; tone: string } | string;
  };
  categories: CategoryItem[];
  onClose: () => void;
  onSubmit: (data: { draft: Draft; file?: File; fileInfo?: FileInfo; version: string; screenshot1?: string; screenshot2?: string }) => Promise<void>;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function nextVersion(current: string): string {
  const parts = current.split('.').map(Number);
  parts[2] = (parts[2] || 0) + 1;
  return parts.join('.');
}

function blankDraft(): Draft {
  return {
    name: '',
    desc: '',
    cat: '',
    features: '',
    note: '',
    visibility: 'public',
    apiUrl: '',
    refresh: '',
    iconChoice: { icon: 'sparkles', tone: 'blue' }
  };
}

export function AppPublishModal({ mode = 'create', existingApp, categories, onClose, onSubmit }: AppPublishModalProps) {
  const editing = mode === 'edit' && !!existingApp;
  const updating = mode === 'update' && !!existingApp;
  const version = updating ? nextVersion(existingApp?.version || '1.0.0') : (existingApp?.version || '1.0.0');

  const [step, setStep] = useState(editing ? 2 : 1);
  const [file, setFile] = useState<File | null>(null);
  const [fileInfo, setFileInfo] = useState<FileInfo | null>(editing ? { kind: '已上传', appType: '网页应用', title: existingApp?.name || '', description: '' } : null);
  const [draft, setDraft] = useState<Draft>(() => {
    if (existingApp) {
      return {
        name: existingApp.name,
        desc: existingApp.desc,
        cat: existingApp.cat,
        features: existingApp.features || '',
        note: '',
        visibility: existingApp.visibility || 'public',
        apiUrl: existingApp.apiUrl || '',
        refresh: existingApp.refresh || '',
        iconChoice: (() => {
          const ic = existingApp.iconChoice;
          if (!ic) return { icon: 'sparkles', tone: 'blue' };
          if (typeof ic === 'string') {
            try { return JSON.parse(ic); } catch { return { icon: 'sparkles', tone: 'blue' }; }
          }
          return ic;
        })()
      };
    }
    return blankDraft();
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [readMode, setReadMode] = useState<'auto' | 'manual'>('auto');
  const [iconOpen, setIconOpen] = useState(false);
  const [iconCategory, setIconCategory] = useState(() => {
    const ic = existingApp?.iconChoice;
    if (!ic) return ICON_GROUPS[0]?.id || '文档知识';
    const choiceObj = typeof ic === 'string' ? (() => { try { return JSON.parse(ic); } catch { return null; } })() : ic;
    const iconName = choiceObj?.icon || 'sparkles';
    for (const g of ICON_GROUPS) {
      if (g.items.some(item => item.icon === iconName)) return g.id;
    }
    return ICON_GROUPS[0]?.id || '文档知识';
  });
  const [newCategory, setNewCategory] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [dynamicOpen, setDynamicOpen] = useState(false);
  const [localCategories, setLocalCategories] = useState<CategoryItem[]>(categories);
  // 截图状态
  const [screenshot1, setScreenshot1] = useState<string | null>(null); // base64预览
  const [screenshot2, setScreenshot2] = useState<string | null>(null);
  const screenshot1Ref = useRef<HTMLInputElement>(null);
  const screenshot2Ref = useRef<HTMLInputElement>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropRef = useRef<HTMLDivElement>(null);

  // 检测文件类型
  const inspectFile = useCallback(async (f: File): Promise<FileInfo> => {
    const name = f.name.toLowerCase();
    if (name.endsWith('.zip')) {
      return { kind: 'ZIP', appType: '压缩包', title: f.name.replace(/\.zip$/i, ''), description: '' };
    }
    if (name.endsWith('.html') || name.endsWith('.htm')) {
      const text = await f.text();
      const titleMatch = text.match(/<title[^>]*>([^<]+)<\/title>/i);
      const descMatch = text.match(/<meta[^>]*name=["']description["'][^>]*content=["']([^"']+)["']/i);
      return {
        kind: 'HTML',
        appType: '网页应用',
        title: titleMatch?.[1]?.trim() || f.name.replace(/\.html?$/i, ''),
        description: descMatch?.[1]?.trim() || ''
      };
    }
    throw new Error('请选择 HTML 或 ZIP 文件');
  }, []);

  const handleFileSelect = useCallback(async (f: File | null) => {
    if (!f) return;
    setBusy(true);
    setError('');
    try {
      const info = await inspectFile(f);
      setFile(f);
      setFileInfo(info);
      setDraft(prev => ({ ...prev, name: info.title || f.name }));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : '文件读取失败');
      setFile(null);
      setFileInfo(null);
    }
    setBusy(false);
  }, [inspectFile]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    dropRef.current?.classList.remove('dragging');
    if (!busy && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  }, [busy, handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (!busy) dropRef.current?.classList.add('dragging');
  }, [busy]);

  const handleDragLeave = useCallback(() => {
    dropRef.current?.classList.remove('dragging');
  }, []);

  const handleReadMetadata = useCallback(async (autoMode: boolean) => {
    if (!fileInfo) return;
    setReadMode(autoMode ? 'auto' : 'manual');
    setStep(2);
    setError('');
    if (autoMode) {
      setBusy(true);
      await new Promise(resolve => setTimeout(resolve, 450));
      setDraft(prev => ({
        ...prev,
        name: fileInfo.title || prev.name,
        desc: fileInfo.description || prev.desc
      }));
      setBusy(false);
    }
  }, [fileInfo]);

  const updateDraft = useCallback(<K extends keyof Draft>(key: K, value: Draft[K]) => {
    setError('');
    setDraft(prev => ({ ...prev, [key]: value }));
  }, []);

  const handleAddCategory = useCallback(() => {
    const name = newCatName.trim();
    if (!name) return;
    const id = 'custom-' + Date.now();
    setLocalCategories(prev => [...prev, { id, name }]);
    setDraft(prev => ({ ...prev, cat: id }));
    setNewCategory(false);
    setNewCatName('');
  }, [newCatName]);

  // 检查必填字段是否有效（用于按钮禁用状态）
  const isRequiredFieldsValid = useCallback((): boolean => {
    return !!(draft.name.trim() && draft.cat && draft.desc.trim());
  }, [draft.name, draft.cat, draft.desc]);

  const validate = useCallback((): boolean => {
    if (!draft.name.trim()) {
      setError('请输入应用名称');
      return false;
    }
    if (!draft.cat) {
      setError('请选择应用分类');
      return false;
    }
    if (!draft.desc.trim()) {
      setError('请输入应用说明');
      return false;
    }
    if (draft.apiUrl && !/^https?:\/\//i.test(draft.apiUrl)) {
      setError('请输入有效的数据接口 URL');
      return false;
    }
    return true;
  }, [draft]);

  const handleSubmit = useCallback(async () => {
    if (busy) return;
    if (step === 1) {
      handleReadMetadata(true);
      return;
    }
    if (step === 2) {
      if (!validate()) return;
      setStep(3);
      return;
    }
    // Step 3: 提交
    setBusy(true);
    try {
      await onSubmit({
        draft,
        file: file || undefined,
        fileInfo: fileInfo || undefined,
        version,
        screenshot1: screenshot1 || undefined,
        screenshot2: screenshot2 || undefined,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : '发布失败');
    }
    setBusy(false);
  }, [busy, step, draft, file, fileInfo, version, screenshot1, screenshot2, validate, handleReadMetadata, onSubmit, onClose]);

  const handleBack = useCallback(() => {
    setError('');
    setStep(prev => Math.max(editing ? 2 : 1, prev - 1));
  }, [editing]);

  // 阻止背景滚动
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // 渲染步骤指示器
  const renderStepper = () => {
    const steps = editing
      ? [[2, '编辑资料'], [3, '确认保存']]
      : [[1, '上传文件'], [2, '填写资料'], [3, '确认发布']];
    return (
      <ol className="pw-steps">
        {steps.map(([s, label], i) => (
          <li key={s} className={step >= (s as number) ? 'active' : ''} aria-current={step === s ? 'step' : undefined}>
            <span>{i + 1}</span>{label}
          </li>
        ))}
      </ol>
    );
  };

  // 渲染文件上传步骤
  const renderFileStep = () => {
    const selected = !!file && !!fileInfo;
    return (
      <>
        <p className="app-archive-hint">
          <Icon name="info" size={17} />
          <span>用 WinRAR 将项目文件压缩为 .zip。</span>
        </p>
        {updating && (
          <p className="pw-version-note">{existingApp?.name} · v{existingApp?.version || '1.0.0'} → v{version}</p>
        )}
        <div
          ref={dropRef}
          className={`pw-drop ${selected ? 'selected' : ''}`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".html,.htm,.zip"
            disabled={busy}
            onChange={e => handleFileSelect(e.target.files?.[0] || null)}
          />
          <span className={`pw-upload-icon ${selected ? 'done' : ''} ${busy ? 'spinning' : ''}`}>
            <Icon name={busy ? 'refresh' : selected ? 'check' : 'upload'} size={26} />
          </span>
          <strong>{busy ? '上传中...' : selected ? file!.name : '选择或拖入 HTML 文件 / ZIP 压缩包'}</strong>
          <p>{selected ? `${formatSize(file!.size)} · 点击重新选择` : '单文件 .html / .htm，或整个应用的 .zip 压缩包 · 最大 300 MB'}</p>
        </div>
      </>
    );
  };

  // 渲染详情填写步骤
  const renderDetailsStep = () => {
    const fallback = readMode === 'auto' && !fileInfo?.title;
    return (
      <>
        <div className={`pw-notice ${fallback && !busy ? 'warning' : ''}`}>
          <span className={`pw-notice-icon ${busy ? 'spinning' : ''}`}>
            <Icon name={busy ? 'refresh' : fallback ? 'info' : 'sparkles'} size={23} />
          </span>
          <div>
            <strong>
              {busy ? '正在读取...' : editing ? '编辑应用信息' : readMode === 'manual' ? '手动填写' : fallback ? '未识别到标题' : '资料读取完成'}
            </strong>
            <p>
              {busy ? '正在提取标题和描述...' : editing ? '修改发布时填写的资料，保存后立即生效' : readMode === 'manual' ? '请填写应用名称、分类和描述' : fallback ? '已使用文件名作为应用名称，建议修改为更清晰的标题' : '已从文件中提取应用资料，请核对并补充信息'}
            </p>
          </div>
        </div>

        {!editing && fileInfo && (
          <div className="pw-file-facts">
            <div><span>文件类型</span><b>{fileInfo.kind}</b></div>
            <div><span>文件大小</span><b>{file ? formatSize(file.size) : '-'}</b></div>
          </div>
        )}

        {!busy && (
          <>
            <p className="pw-version-note">{editing ? '当前版本' : '发布版本'} · v{version}</p>
            
            <div className="pw-section-label">
              <strong>发布内容</strong>
              <span>{editing ? '可修改应用信息' : readMode === 'manual' ? '请填写应用信息' : fallback ? '请修改应用名称并补充信息' : '自动填写，也可以手动修改'}</span>
            </div>

            {/* 图标选择器 */}
            {/* ===== 图标选择器 - 完全对照原型 ===== */}
            <fieldset className="icon-picker" data-icon-picker>
              <legend>应用图标</legend>

              {/* 摘要行：预览 + 名称/分类 + 按钮 */}
              <div className="icon-picker-summary">
                <span className="icon-picker-preview">
                  {/* app-icon 组件，使用原型配色 */}
                  <span
                    className="app-icon"
                    style={{
                      '--app-icon-size': '40px',
                      background: APP_ICON_TONES[draft.iconChoice.tone]?.gradient || APP_ICON_TONES.blue.gradient,
                      color: APP_ICON_TONES[draft.iconChoice.tone]?.color || '#fff',
                    } as React.CSSProperties}
                  >
                    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.45" strokeLinecap="round" strokeLinejoin="round">
                      <path d={APP_ICON_PATHS[draft.iconChoice.icon] || APP_ICON_PATHS.sparkles} />
                    </svg>
                  </span>
                </span>
                <div className="icon-picker-description">
                  <strong>{ICON_BY_KEY.get(
                    ICON_GROUPS.flatMap(g => g.items).find(x => x.icon === draft.iconChoice.icon)?.key || ''
                  )?.label || draft.iconChoice.icon}</strong>
                  <span>{ICON_GROUPS.find(g => g.items.some(x => x.icon === draft.iconChoice.icon))?.label || '图标分类'}</span>
                </div>
                <button
                  type="button"
                  className="btn btn-secondary icon-picker-toggle"
                  aria-expanded={iconOpen}
                  onClick={() => setIconOpen(o => !o)}
                >
                  {iconOpen ? '收起' : '选择图标'}
                </button>
              </div>

              {/* 展开选项区 */}
              {iconOpen && (
                <div className="icon-picker-options">
                  {/* 分类标签 */}
                  <div className="icon-category-tabs" role="tablist" aria-label="图标板块">
                    {ICON_GROUPS.map(g => (
                      <button
                        key={g.id}
                        type="button"
                        role="tab"
                        aria-selected={iconCategory === g.id}
                        onClick={() => setIconCategory(g.id)}
                      >
                        {g.label}
                      </button>
                    ))}
                  </div>

                  {/* 图标网格 */}
                  {ICON_GROUPS.filter(g => g.id === iconCategory).map(g => (
                    <div key={g.id} className="icon-choice-grid" role="tabpanel">
                      {g.items.map(item => {
                        const isSelected = draft.iconChoice.icon === item.icon &&
                          ICON_GROUPS.find(gr => gr.id === iconCategory)?.id === iconCategory;
                        return (
                          <button
                            key={item.key}
                            type="button"
                            className={`icon-choice ${isSelected ? 'selected' : ''}`}
                            aria-pressed={isSelected}
                            aria-label={item.label}
                            onClick={() => updateDraft('iconChoice', { ...draft.iconChoice, icon: item.icon })}
                          >
                            <span
                              className="app-icon"
                              style={{
                                '--app-icon-size': '36px',
                                background: APP_ICON_TONES[draft.iconChoice.tone]?.gradient || APP_ICON_TONES.blue.gradient,
                                color: APP_ICON_TONES[draft.iconChoice.tone]?.color || '#fff',
                              } as React.CSSProperties}
                            >
                              <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.45" strokeLinecap="round" strokeLinejoin="round">
                                <path d={APP_ICON_PATHS[item.icon] || APP_ICON_PATHS.apps} />
                              </svg>
                            </span>
                            <span>{item.label}</span>
                            <i className="icon-choice-check">
                              <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" width={11} height={11}>
                                <path d="m4 10 4 4 8-9"/>
                              </svg>
                            </i>
                          </button>
                        );
                      })}
                    </div>
                  ))}

                  {/* 配色行 */}
                  <div className="icon-tone-row">
                    <span>配色</span>
                    <div className="icon-tone-options" role="group" aria-label="图标配色">
                      {ICON_TONES.map(t => (
                        <button
                          key={t.id}
                          type="button"
                          className={`icon-tone tone-${t.id} ${draft.iconChoice.tone === t.id ? 'selected' : ''}`}
                          aria-label={t.label}
                          title={t.label}
                          aria-pressed={draft.iconChoice.tone === t.id}
                          style={{ background: t.bg, color: t.color }}
                          onClick={() => updateDraft('iconChoice', { ...draft.iconChoice, tone: t.id })}
                        >
                          <svg className="icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" width={12} height={12}>
                            <path d="m4 10 4 4 8-9"/>
                          </svg>
                        </button>
                      ))}
                    </div>
                  </div>
                  <p className="icon-picker-help">从分类图标库中选择，可随时更换。</p>
                </div>
              )}
            </fieldset>

            <div className="pw-fields">
              {/* 应用名称 */}
              <label className="pw-field">
                <span>应用名称 <em className="pw-required"><span>必填</span></em></span>
                <input
                  type="text"
                  maxLength={100}
                  value={draft.name}
                  onChange={e => updateDraft('name', e.target.value)}
                  placeholder="例如：新品定价测算器"
                />
                <small className="pw-counter">{draft.name.length}/100</small>
              </label>

              {/* 应用分类 */}
              <div className="pw-field">
                <label htmlFor="pw-category">应用分类 <em className="pw-required"><span>必选</span></em></label>
                <div className="pw-category-line">
                  <span className="pw-select-wrap">
                    <select
                      id="pw-category"
                      value={draft.cat}
                      onChange={e => updateDraft('cat', e.target.value)}
                    >
                      <option value="">请选择应用分类</option>
                      {localCategories.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                    <Icon name="down" size={16} />
                  </span>
                  <button type="button" className="btn btn-secondary" onClick={() => setNewCategory(!newCategory)}>
                    ＋ 新建分类
                  </button>
                </div>
                {newCategory && (
                  <div className="pw-create-category">
                    <input
                      maxLength={20}
                      placeholder="输入分类名称"
                      value={newCatName}
                      onChange={e => setNewCatName(e.target.value)}
                    />
                    <button type="button" className="btn btn-primary" disabled={!newCatName.trim()} onClick={handleAddCategory}>添加</button>
                    <button type="button" className="btn btn-secondary" onClick={() => { setNewCategory(false); setNewCatName(''); }}>取消</button>
                  </div>
                )}
              </div>

              {/* 应用说明 */}
              <label className="pw-field wide">
                <span>应用说明 <em className="pw-required"><span>必填</span></em></span>
                <textarea
                  maxLength={500}
                  value={draft.desc}
                  onChange={e => updateDraft('desc', e.target.value)}
                  placeholder="简要介绍应用的用途和功能"
                />
                <small className="pw-counter">{draft.desc.length}/500</small>
              </label>

              {/* 功能亮点 */}
              <label className="pw-field wide">
                <span>功能亮点</span>
                <input
                  type="text"
                  maxLength={80}
                  value={draft.features}
                  onChange={e => updateDraft('features', e.target.value)}
                  placeholder="用顿号分隔，如：数据可视化、实时更新、导出报表"
                />
              </label>

              {/* 本次更新说明 */}
              <label className="pw-field wide">
                <span>本次更新说明 <em>人工确认</em></span>
                <input
                  type="text"
                  maxLength={80}
                  value={draft.note}
                  onChange={e => updateDraft('note', e.target.value)}
                  placeholder="例如：首次发布 / 调整筛选逻辑 / 修复移动端显示"
                />
                <small className="pw-counter">{draft.note.length}/80</small>
              </label>

              {/* 可见范围 */}
              <div className="pw-field wide">
                <label>可见范围 <em>人工选择</em></label>
                <div className="pw-visibility">
                  <button
                    type="button"
                    className={draft.visibility === 'public' ? 'active' : ''}
                    onClick={() => updateDraft('visibility', 'public')}
                  >
                    <Icon name="globe" size={22} />
                    <span><strong>公用</strong><small>全公司可见</small></span>
                  </button>
                  <button
                    type="button"
                    className={draft.visibility === 'dept' ? 'active' : ''}
                    onClick={() => updateDraft('visibility', 'dept')}
                  >
                    <Icon name="lock" size={22} />
                    <span><strong>本部门</strong><small>仅本部门可见</small></span>
                  </button>
                </div>
              </div>
            </div>

            {/* 截图预览上传 */}
            <div className="pw-section-label" style={{ marginTop: 20 }}>
              <strong>预览截图</strong>
              <span>最多两张，展示在应用详情中（可选）</span>
            </div>
            <div className="pw-screenshots">
              {/* 截图1 */}
              <div className="pw-screenshot-slot">
                <input ref={screenshot1Ref} type="file" accept="image/jpeg,image/png,image/webp"
                  style={{ display: 'none' }}
                  onChange={e => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    const reader = new FileReader();
                    reader.onload = ev => setScreenshot1(ev.target?.result as string);
                    reader.readAsDataURL(f);
                    e.target.value = '';
                  }}
                />
                {screenshot1 ? (
                  <div className="pw-screenshot-preview">
                    <img src={screenshot1} alt="截图1" />
                    <button type="button" className="pw-screenshot-remove" onClick={() => setScreenshot1(null)}>×</button>
                  </div>
                ) : (
                  <button type="button" className="pw-screenshot-add" onClick={() => screenshot1Ref.current?.click()}>
                    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.45" strokeLinecap="round" strokeLinejoin="round" width={22} height={22}><path d="M10 3v14M3 10h14"/></svg>
                    <span>添加截图 1</span>
                  </button>
                )}
              </div>
              {/* 截图2 */}
              <div className="pw-screenshot-slot">
                <input ref={screenshot2Ref} type="file" accept="image/jpeg,image/png,image/webp"
                  style={{ display: 'none' }}
                  onChange={e => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    const reader = new FileReader();
                    reader.onload = ev => setScreenshot2(ev.target?.result as string);
                    reader.readAsDataURL(f);
                    e.target.value = '';
                  }}
                />
                {screenshot2 ? (
                  <div className="pw-screenshot-preview">
                    <img src={screenshot2} alt="截图2" />
                    <button type="button" className="pw-screenshot-remove" onClick={() => setScreenshot2(null)}>×</button>
                  </div>
                ) : (
                  <button type="button" className="pw-screenshot-add" onClick={() => screenshot2Ref.current?.click()}>
                    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.45" strokeLinecap="round" strokeLinejoin="round" width={22} height={22}><path d="M10 3v14M3 10h14"/></svg>
                    <span>添加截图 2</span>
                  </button>
                )}
              </div>
            </div>

            {/* 动态数据配置 */}
            <details className="pw-dynamic" open={dynamicOpen} onToggle={e => setDynamicOpen((e.target as HTMLDetailsElement).open)}>
              <summary>动态数据配置（可选）</summary>
              <div className="pw-fields">
                <label className="pw-field">
                  <span>数据接口 URL</span>
                  <input
                    type="url"
                    value={draft.apiUrl}
                    onChange={e => updateDraft('apiUrl', e.target.value)}
                    placeholder="https://api.example.com/data"
                  />
                </label>
                <label className="pw-field">
                  <span>自动刷新间隔（秒）</span>
                  <input
                    type="number"
                    min={0}
                    value={draft.refresh}
                    onChange={e => updateDraft('refresh', e.target.value)}
                    placeholder="0 = 不自动刷新"
                  />
                </label>
              </div>
            </details>

            <p className="pw-system-note">
              <Icon name="check" size={16} />
              <span>系统自动填写：发布人、所属团队、版本号、更新时间和打开次数。</span>
            </p>
          </>
        )}
      </>
    );
  };

  // 渲染确认步骤
  const renderConfirmStep = () => {
    const catName = localCategories.find(c => c.id === draft.cat)?.name || '未分类';
    return (
      <>
        <div className="pw-confirm-title">
          <strong>{editing ? '确认保存信息' : '确认发布信息'}</strong>
          <p>{editing ? '请核对修改内容，确认后保存' : '请核对以下信息，确认无误后点击发布'}</p>
        </div>

        {!editing && file && fileInfo && (
          <section className="pw-confirm-section">
            <h3>文件信息</h3>
            <dl>
              <div><dt>文件名</dt><dd>{file.name}</dd></div>
              <div><dt>文件类型</dt><dd>{fileInfo.kind}</dd></div>
              <div><dt>文件大小</dt><dd>{formatSize(file.size)}</dd></div>
              <div><dt>应用类型</dt><dd>{fileInfo.appType}</dd></div>
            </dl>
          </section>
        )}

        <section className="pw-confirm-section">
          <h3>应用信息</h3>
          <dl>
            <div className="wide">
              <dt>应用名称</dt>
              <dd className="pw-confirm-app">
                <span
                  className="confirm-icon"
                  style={{
                    background: ICON_TONES.find(t => t.id === draft.iconChoice.tone)?.bg || '#247cf2',
                    color: ICON_TONES.find(t => t.id === draft.iconChoice.tone)?.color || 'white',
                  }}
                >
                  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.45" strokeLinecap="round" strokeLinejoin="round" width={18} height={18}>
                    <path d={APP_ICON_PATHS[draft.iconChoice.icon] || APP_ICON_PATHS.sparkles} />
                  </svg>
                </span>
                <span>{draft.name}</span>
              </dd>
            </div>
            <div><dt>版本号</dt><dd>v{version}</dd></div>
            <div><dt>应用分类</dt><dd>{catName}</dd></div>
            <div><dt>可见范围</dt><dd>{draft.visibility === 'dept' ? '本部门' : '全公司'}</dd></div>
            {draft.desc && <div className="wide"><dt>应用说明</dt><dd>{draft.desc}</dd></div>}
            {draft.features && <div className="wide"><dt>功能亮点</dt><dd>{draft.features}</dd></div>}
            {draft.note && <div className="wide"><dt>本次更新说明</dt><dd>{draft.note}</dd></div>}
          </dl>
        </section>

        {draft.apiUrl && (
          <section className="pw-confirm-section">
            <h3>动态数据配置</h3>
            <dl>
              <div className="wide"><dt>数据接口 URL</dt><dd>{draft.apiUrl}</dd></div>
              <div><dt>自动刷新间隔</dt><dd>{Number(draft.refresh) > 0 ? `${draft.refresh} 秒` : '不自动刷新'}</dd></div>
            </dl>
          </section>
        )}

        <section className="pw-confirm-section">
          <h3>发布设置</h3>
          <p>
            <Icon name="globe" size={17} />
            {draft.visibility === 'dept' ? '本部门成员可直接查看和下载' : '企业成员可直接查看和下载'}
          </p>
        </section>

        <button type="button" className="pw-edit" onClick={handleBack}>
          <Icon name="edit" size={16} />
          返回修改
        </button>
      </>
    );
  };

  return (
    <div className="modal-mask open" onClick={onClose}>
      <section className="dialog wide app-publish-dialog" onClick={e => e.stopPropagation()}>
        <header className="dialog-head">
          <div className="pw-heading">
            <span><Icon name="upload" size={23} /></span>
            <div>
              <h2>{editing ? '编辑信息' : updating ? '更新版本' : '上传企业应用'}</h2>
              <p>{editing ? '编辑应用资料，确认后保存' : updating ? '上传新版本文件后自动生成应用资料，确认后发布' : '上传文件后自动生成应用资料，确认后发布'}</p>
            </div>
          </div>
          <button type="button" className="dialog-close" onClick={onClose}>
            <Icon name="close" size={18} />
          </button>
        </header>

        <div className="dialog-body">
          {renderStepper()}
          {error && <p className="pw-error">{error}</p>}
          {step === 1 && renderFileStep()}
          {step === 2 && renderDetailsStep()}
          {step === 3 && renderConfirmStep()}
        </div>

        <footer className="dialog-footer">
          {step > (editing ? 2 : 1) && (
            <button type="button" className="btn btn-secondary" disabled={busy} onClick={handleBack}>上一步</button>
          )}
          <button type="button" className="btn btn-secondary" onClick={onClose}>取消</button>
          {step === 1 && (
            <button type="button" className="btn btn-secondary" disabled={!fileInfo || busy} onClick={() => handleReadMetadata(false)}>直接填写</button>
          )}
          {!(step === 2 && busy) && (
            <button
              type="button"
              className="btn btn-primary"
              disabled={busy || (step === 1 && !fileInfo) || (step === 2 && !isRequiredFieldsValid())}
              onClick={handleSubmit}
            >
              {busy ? '发布中…' : step === 1 ? '自动读取资料' : step === 2 ? '下一步' : editing ? '保存修改' : '确认发布'}
            </button>
          )}
        </footer>
      </section>
    </div>
  );
}

export default AppPublishModal;
